import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaXmark } from "react-icons/fa6";

export function useModal() {
	const [open, setOpen] = useState(false);
	const onClose = useCallback(() => setOpen(false), []);
	const onOpen = useCallback(() => setOpen(true), []);
	return { open, setOpen, onClose, onOpen };
}

const ModalSizes = {
	sm: "min-w-[24em] max-w-[40em]",
	md: "min-w-[30em] max-w-[48em]",
	lg: "min-w-[36em] max-w-[56em]",
	xl: "min-w-[40em] max-w-[64em]",
};

export default function Modal({
	size = "md",
	open,
	onClose,
	children,
	canClose = true,
}: React.PropsWithChildren<{
	open: boolean;
	onClose: () => void;
	size?: keyof typeof ModalSizes;
	canClose?: boolean;
}>) {
	const modalClassNames = ModalSizes[size];
	const innerEl = useRef<HTMLDivElement>(null);
	const wrappedOnClose = useCallback(
		(e: React.MouseEvent) => {
			if (innerEl.current && !innerEl.current.contains(e.target as Node))
				canClose && onClose();
		},
		[onClose, canClose],
	);

	// Handle escape key press to close modal
	useEffect(() => {
		const handleEscapeKey = (e: KeyboardEvent) => {
			if (open && canClose && e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscapeKey);

		// Clean up event listener on unmount or when dependencies change
		return () => {
			document.removeEventListener("keydown", handleEscapeKey);
		};
	}, [open, canClose, onClose]);

	if (!open) return null;
	return (
		<>
			{createPortal(
				// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
				<section
					className="fixed flex items-center justify-center p-9 top-0 left-0 w-screen h-screen isolate bg-black/75 z-70"
					onClick={wrappedOnClose}
				>
					<div
						ref={innerEl}
						className={`relative border-amber-500/50 border-1 min-w-[90vw] sm:min-w-[24em] md:min-w-[30em] min-h-[1rem] max-h-[90vh] bg-dark-blue-dark rounded-xl p-6 shadow-xl z-80 animate-modal overflow-y-scroll ${modalClassNames}`}
					>
						{children}
						<button
							type="button"
							onClick={onClose}
							className="text-3xl absolute top-3 right-3 shadow-md cursor-pointer"
						>
							<FaXmark />
						</button>
					</div>
				</section>,
				document.body,
			)}
		</>
	);
}
